import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Spacing from '../Spacing';
import SectionHeadingStyle3 from '../SectionHeading/SectionHeadingStyle3';
import PostStyle4 from '../Post/PostStyle4';
import Sidebar from '../Sidebar';
import Pagination from '../Pagination';
import ItemsPerPageSelector from '../Pagination/ItemsPerPageSelector';
import SEO from '../SEO';
import { pageTitle } from '../../helpers/PageTitle';
import { useCategories } from '../Providers/CategoryContext';
import authService from '../../services/authService';
import axios from 'axios';

export default function BlogPage() {
  const [blogs, setBlogs] = useState([])
  const [totalCount, setTotalCount] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedCategory = searchParams.get('category');
  const currentPage = parseInt(searchParams.get('page')) || 1;
  const itemsPerPage = parseInt(searchParams.get('perPage')) || 10;
  const { categories } = useCategories();
  const [currentUser, setCurrentUser] = useState(null);
  pageTitle('Blog');

  useEffect(() => {
    const fetchCurrentUser = async () => {
      if (authService.isAuthenticated()) {
        const user = await authService.getCurrentUser();
        setCurrentUser(user);
      }
    };
    fetchCurrentUser();
  }, []);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const offset = (currentPage - 1) * itemsPerPage;
        let url = `${import.meta.env.REACT_APP_CMS_URL}/items/posts?fields=*,category.name&filter[status][_eq]=published`;

        if (selectedCategory) {
          url += `&filter[category][_eq]=${selectedCategory}`;
        }

        url += `&sort=-date_created&limit=${itemsPerPage}&offset=${offset}&meta=filter_count`;

        const res = await fetch(url);
        if (!res.ok) throw new Error('Failed to load posts');
        const result = await res.json();
        const posts = result.data || [];

        // Fetch like counts and user likes for each post
        const postsWithLikes = await Promise.all(
          posts.map(async (post) => {
            try {
              // Get like count
              const likeCountRes = await axios.get(
                `${import.meta.env.REACT_APP_CMS_URL}/items/post_likes?filter[post][_eq]=${post.id}&aggregate[count]=id`
              );
              const likeCount = likeCountRes.data.data?.[0]?.count?.id || 0;

              // Check if current user has liked this post
              let isLiked = false;
              let likeId = null;
              if (currentUser) {
                const token = authService.getAccessToken();
                const userLikeRes = await axios.get(
                  `${import.meta.env.REACT_APP_CMS_URL}/items/post_likes?filter[post][_eq]=${post.id}&filter[user][_eq]=${currentUser.id}&limit=1`,
                  { headers: { Authorization: `Bearer ${token}` } }
                );
                const userLikes = userLikeRes.data.data;
                if (userLikes && userLikes.length > 0) {
                  isLiked = true;
                  likeId = userLikes[0].id;
                }
              }

              return {
                ...post,
                likeCount,
                isLiked,
                likeId,
              };
            } catch (error) {
              console.error(`Error fetching likes for post ${post.id}:`, error);
              return {
                ...post,
                likeCount: 0,
                isLiked: false,
                likeId: null,
              };
            }
          })
        );

        setBlogs(postsWithLikes);
        setTotalCount(result.meta?.filter_count || 0);
      } catch (error) {
        console.error('Error fetching blogs:', error);
        setBlogs([]);
        setTotalCount(0);
      }
    }
    fetchBlogs()
  },[selectedCategory, currentPage, itemsPerPage, currentUser])

  const mapCategory = (categoryData) => {
    if (categoryData && typeof categoryData === 'object' && categoryData.name) {
      return categoryData.name;
    }
    if (categoryData && typeof categoryData === 'number') {
      const result = categories.find(item => item.id === categoryData);
      return result ? result.name : 'Uncategorized';
    }
    return 'Uncategorized';
  }

  const getSelectedCategoryName = () => {
    if (!selectedCategory) return null;
    const category = categories.find(cat => cat.id === parseInt(selectedCategory));
    return category ? category.name : null;
  }

  const categoryName = getSelectedCategoryName();
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const getSEOTitle = () => {
    if (categoryName) {
      return currentPage > 1 
        ? `${categoryName} Posts - Page ${currentPage}`
        : `${categoryName} Posts`;
    }
    return currentPage > 1 
      ? `Blog - Page ${currentPage}`
      : 'Blog';
  };

  const getSEODescription = () => {
    if (categoryName) {
      return `Explore our latest ${categoryName.toLowerCase()} articles, tutorials, and insights. Stay updated with expert content on software development, technology, and digital innovation.`;
    }
    return 'Explore our blog for the latest articles on software development, web development, mobile apps, UI/UX design, and technology trends. Expert insights and tutorials from DigitalByte.';
  };

  const handlePageChange = (page) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', page);
    setSearchParams(params);
  };

  const handleItemsPerPageChange = (perPage) => {
    const params = new URLSearchParams(searchParams);
    params.set('perPage', perPage);
    params.set('page', 1);
    setSearchParams(params);
  };

  const handleLikeChange = (postId, isLiked, likeId, newCount) => {
    setBlogs(prevBlogs =>
      prevBlogs.map(blog =>
        blog.id === postId
          ? { ...blog, isLiked, likeId, likeCount: newCount }
          : blog
      )
    );
  };

  return (
    <>
      <SEO
        title={getSEOTitle()}
        description={getSEODescription()}
        keywords="blog, software development, web development, mobile apps, technology, tutorials, programming"
        url={`/blog${selectedCategory ? `?category=${selectedCategory}` : ''}${currentPage > 1 ? `&page=${currentPage}` : ''}`}
        type="website"
      />
      <Spacing lg="70" md="70" />
      <Spacing lg="140" md="80" />
      <SectionHeadingStyle3
        title={categoryName ? `${categoryName} Posts` : "Stay up to date with our <br>top blog post"}
        subTitle={categoryName ? `Filtered by ${categoryName}` : "Top Posts"}
        variant="text-center"
        shape="shape_5"
      />
      <Spacing lg="75" md="60" />
      <div className="container">
        <div className="row cs_gap_y_60">
          <div className="col-lg-8">
            {totalCount > 0 && (
              <ItemsPerPageSelector
                itemsPerPage={itemsPerPage}
                onItemsPerPageChange={handleItemsPerPageChange}
                totalItems={totalCount}
              />
            )}
            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                itemsPerPage={itemsPerPage}
                onItemsPerPageChange={handleItemsPerPageChange}
                totalItems={totalCount}
                showItemsPerPage={false}
              />
            )}
            {blogs.map((item) => {
              const getCategoryId = () => {
                if (!item.category) return null;
                if (typeof item.category === 'object' && item.category !== null) {
                  return item.category.id;
                }
                return item.category;
              };

              return (
                <PostStyle4
                  key={item.id}
                  thumbnailSrc={`${import.meta.env.REACT_APP_CMS_URL}/assets/${item.hero_image}`}
                  category={mapCategory(item.category)}
                  categoryId={getCategoryId()}
                  title={item.title}
                  subTitle={item.sub_title}
                  id={item.id}
                  likeCount={item.likeCount || 0}
                  isLiked={item.isLiked || false}
                  likeId={item.likeId}
                  onLikeChange={handleLikeChange}
                />
              );
            })}
            {blogs.length === 0 && (
              <div className="text-center">
                <Spacing lg="50" md="30" />
                <p style={{ fontSize: '18px', color: '#666' }}>No posts found in this category.</p>
                <Spacing lg="50" md="30" />
              </div>
            )}
            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                itemsPerPage={itemsPerPage}
                onItemsPerPageChange={handleItemsPerPageChange}
                totalItems={totalCount}
              />
            )}
          </div>
          <div className="col-lg-3 offset-lg-1">
            <Sidebar selectedCategory={selectedCategory} />
          </div>
        </div>
        <Spacing lg="150" md="80" />
      </div>
    </>
  );
}
